import React, { useState, useEffect, useRef, useCallback, useMemo, Suspense } from 'react';
import PropTypes from 'prop-types';
import { Responsive, WidthProvider } from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import GoogleImageDisplay from './GoogleImageDisplay.jsx';
import { formatDefinitionText } from '../utils/wikipedia.js';
import { useSidebar } from '../contexts/SidebarContext.jsx';
import '../styles/insights-sidebar.css';

// Normalize and clean news URLs (strip Google tracking and malformed query strings)
const TRACKING_PARAMS = new Set([
    'ved', 'usg', 'sei', 'source', 'ei', 'oq', 'aqs', 'sca_esv', 'uact', 'sclient', 'gws_rd', 'ust', 'at', 'rct', 'tbm', 'gclid', 'gclsrc'
]);

const cleanNewsLink = (link) => {
    if (!link || typeof link !== 'string') return link;
    let urlStr = link.trim();

    // Protocol-relative URLs
    if (urlStr.startsWith('//')) {
        urlStr = 'https:' + urlStr;
    }

    // If tracking params are appended without '?', cut them at first '&ved='
    if (urlStr.includes('&ved=') && !urlStr.includes('?')) {
        return urlStr.split('&ved=', 1)[0];
    }

    // If there are '&' but no '?', convert the first '&' to '?'
    if (!urlStr.includes('?') && urlStr.includes('&')) {
        const idx = urlStr.indexOf('&');
        urlStr = urlStr.slice(0, idx) + '?' + urlStr.slice(idx + 1);
    }

    try {
        const urlObj = new URL(urlStr);
        // Remove known tracking params and utm_*
        Array.from(urlObj.searchParams.keys()).forEach((key) => {
            if (TRACKING_PARAMS.has(key) || key.startsWith('utm_')) {
                urlObj.searchParams.delete(key);
            }
        });
        urlObj.hash = '';
        return urlObj.toString();
    } catch (e) {
        return link; // Fallback to original
    }
};

// Validate news image (skip 1x1 gif placeholders and empty values)
const isValidNewsImage = (src) => {
    if (!src || typeof src !== 'string') return false;
    const s = src.trim();
    if (!s) return false;
    if (s.startsWith('data:image/gif;base64,R0lGODlhAQABAIAA')) return false;
    return true;
};

const getHostnameFromLink = (link) => {
    try {
        const cleaned = cleanNewsLink(link);
        const u = new URL(cleaned);
        return u.hostname || null;
    } catch (e) {
        return null;
    }
};

const getFaviconUrl = (link) => {
    const host = getHostnameFromLink(link);
    if (!host) return null;
    return `https://www.google.com/s2/favicons?domain=${host}&sz=64`;
};

const getNewsThumbnailSrc = (article) => {
    if (isValidNewsImage(article?.img)) return article.img;
    const link = article?.link || article?.url;
    const favicon = getFaviconUrl(link);
    return favicon;
};

// Lazy load the chart component for better performance
const TrendChart = React.lazy(() => import('../../pages/analyze/components/TrendChart.jsx'));

const ResponsiveGridLayout = WidthProvider(Responsive);

const InsightsSidebar = ({ isAuthenticated, isStacked = false, parentIsOpen = false, onParentClose = null }) => {
    // --- Use Sidebar Context ---
    const {
        activeItem,
        isOpen: contextIsOpen,
        hasUserClosed,
        loadingStates,
        errorStates,
        cachedData,
        selectedTickers,
        userPreferences,
        setActiveItem,
        setIsOpen,
        setUserClosed,
        setSelectedTickers,
        fetchCompanyProfile,
        fetchCompanyLogo,
        fetchRelatedItems,
        fetchRelatedNews,
        fetchAnalystExpectations,
        fetchRevenueSegmentation,
        fetchAnalystSentiment,
        fetchKeywordDefinition
    } = useSidebar();
    
    // --- Subscription Check ---
    const userTier = window.userTier || 'anonymous';
    const isLoggedIn = window.isLoggedIn || false;
    
    // Debug logging
    console.log('🔍 InsightsSidebar - Debug Info:', {
        userTier,
        isLoggedIn,
        windowUserTier: window.userTier,
        windowIsLoggedIn: window.isLoggedIn
    });
    
    // Tier hierarchy (kept for potential future use; Insights open to all tiers)
    const tierLevels = {
        'anonymous': 0,
        'free': 1,
        'professional': 2,
        'enterprise': 3
    };
    
    const userLevel = tierLevels[userTier] || 0;
    const hasInsightsAccess = true;
    
    console.log('🔍 InsightsSidebar - Access Check:', {
        userLevel,
        requiredLevel: tierLevels.professional,
        hasInsightsAccess
    });
    
    // --- Local State (UI-specific) ---
    const contentRef = useRef(null);
    const [internalIsOpen, setInternalIsOpen] = useState(false);
    const [failedLogos, setFailedLogos] = useState(new Set()); // Track failed logo URLs
    const [loadedImages, setLoadedImages] = useState(new Set()); // Track successfully loaded images
    const [chartError, setChartError] = useState(null);

    // --- Derived Data from Context (memoized to prevent infinite re-renders) ---
    const companyProfile = useMemo(() => 
        activeItem?.type === 'company' ? cachedData.companyProfiles[activeItem.data.symbol] : null,
        [activeItem, cachedData.companyProfiles]
    );
    
    const companyLogo = useMemo(() => 
        activeItem?.type === 'company' ? cachedData.companyLogos[activeItem.data.symbol] : null,
        [activeItem, cachedData.companyLogos]
    );
    
    const relatedKeywords = useMemo(() => 
        activeItem?.type === 'keyword' ? cachedData.relatedItems[activeItem.data.keyword_name]?.related_keywords || [] : [],
        [activeItem, cachedData.relatedItems]
    );
    
    const relatedCompanies = useMemo(() => 
        activeItem?.type === 'keyword' ? cachedData.relatedItems[activeItem.data.keyword_name]?.related_companies || [] : [],
        [activeItem, cachedData.relatedItems]
    );
    
    const relatedNews = useMemo(() => 
        activeItem ? cachedData.news[activeItem.type === 'company' ? activeItem.data.symbol : activeItem.data.keyword_name] || [] : [],
        [activeItem, cachedData.news]
    );
    
    const keywordDefinition = useMemo(() => 
        activeItem?.type === 'keyword' ? cachedData.keywordDefinitions[activeItem.data.keyword_name] : null,
        [activeItem, cachedData.keywordDefinitions]
    );
    
    const analystExpectations = useMemo(() => 
        activeItem?.type === 'company' ? cachedData.analystExpectations[`${activeItem.data.symbol}_current`] : null,
        [activeItem, cachedData.analystExpectations]
    );
    
    const revenueSegments = useMemo(() => 
        activeItem?.type === 'company' ? cachedData.revenueSegments[activeItem.data.symbol] || { latestLabel: null, rows: [] } : { latestLabel: null, rows: [] },
        [activeItem, cachedData.revenueSegments]
    );
    
    const sentimentData = useMemo(() => 
        cachedData.sentimentData || {},
        [cachedData.sentimentData]
    );

    // --- Chart and UI State ---
    const chartRef = useRef(null);
    const [scrollThumb, setScrollThumb] = useState({ height: 40, top: 0, visible: false });
    const dragStateRef = useRef({ dragging: false, startY: 0, startTop: 0, ratio: 1 });
    
    // Track the last processed item to prevent duplicate requests
    const lastProcessedRef = useRef(null);

    const recalcThumb = useCallback(() => {
        const el = contentRef.current;
        if (!el) return;
        const { scrollHeight, clientHeight, scrollTop } = el;
        const canScroll = scrollHeight > clientHeight + 2;
        if (!canScroll) {
            setScrollThumb(prev => ({ ...prev, visible: false, top: 0 }));
            return;
        }
        const track = clientHeight;
        const thumbHeight = Math.max(28, Math.floor((clientHeight * clientHeight) / scrollHeight));
        const maxThumbTop = Math.max(0, track - thumbHeight);
        const thumbTop = Math.min(maxThumbTop, Math.floor((scrollTop / (scrollHeight - clientHeight)) * maxThumbTop));
        const ratio = (scrollHeight - clientHeight) / Math.max(1, (track - thumbHeight));
        dragStateRef.current.ratio = ratio;
        setScrollThumb({ height: thumbHeight, top: thumbTop, visible: true });
    }, []);

    const handleOuterScroll = useCallback(() => {
        recalcThumb();
    }, [recalcThumb]);

    useEffect(() => {
        recalcThumb();
    }, [activeItem, layouts, relatedNews, keywordDefinition, sentimentData, analystExpectations, revenueSegments, recalcThumb]);

    useEffect(() => {
        const onResize = () => recalcThumb();
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, [recalcThumb]);

    useEffect(() => {
        const onMouseMove = (e) => {
            if (!dragStateRef.current.dragging || !contentRef.current) return;
            e.preventDefault();
            const delta = e.clientY - dragStateRef.current.startY;
            const newTop = Math.max(0, dragStateRef.current.startTop + delta);
            const el = contentRef.current;
            const { clientHeight } = el;
            const maxTop = Math.max(0, clientHeight - scrollThumb.height);
            const clampedTop = Math.min(maxTop, newTop);
            el.scrollTop = clampedTop * dragStateRef.current.ratio;
        };
        const onMouseUp = () => { dragStateRef.current.dragging = false; };
        document.addEventListener('mousemove', onMouseMove);
        document.addEventListener('mouseup', onMouseUp);
        return () => {
            document.removeEventListener('mousemove', onMouseMove);
            document.removeEventListener('mouseup', onMouseUp);
        };
    }, [scrollThumb.height]);

    const startDragThumb = useCallback((e) => {
        if (!contentRef.current) return;
        dragStateRef.current.dragging = true;
        dragStateRef.current.startY = e.clientY;
        dragStateRef.current.startTop = scrollThumb.top;
        e.preventDefault();
    }, [scrollThumb.top]);
    
    // --- Memoize logo URL at component level to prevent hook violations ---
    const stableLogoUrl = useMemo(() => companyLogo?.logoUrl, [companyLogo?.logoUrl]);

    // --- Grid Layout State ---
    const [layouts, setLayouts] = useState(null);
    const [isLayoutUpdating, setIsLayoutUpdating] = useState(false);

    // Fixed component heights - easy to adjust
    const COMPONENT_HEIGHTS = {
        chart: { minH: 4, defaultH: 4 },
        overview: { minH: 4, defaultH: 4.3 },
        related: { minH: 2, defaultH: 2.5 },
        news: { minH: 2, defaultH: 3 },
        expectations: { minH: 3, defaultH: 3 },
        revenue: { minH: 3, defaultH: 3 },
        sentiment: { minH: 2, defaultH: 3 },
        image: { minH: 2, defaultH: 2.5 },
        definition: { minH: 2, defaultH: 2.5 }
    };

    // Define grid components
    const gridComponents = useMemo(() => {
        const components = [];

        // Only add Company Overview for company selections
        if (activeItem?.type === 'company') {
            components.push({
                id: 'overview',
                title: 'Company Overview',
                component: 'CompanyOverview',
                ...COMPONENT_HEIGHTS.overview
            });
        }

        // Add Google Image for keywords only
        if (activeItem?.type === 'keyword') {
            components.push({
                id: 'image',
                title: 'Visual Context',
                component: 'GoogleImageSection',
                ...COMPONENT_HEIGHTS.image
            });
        }

        // Add Definition for keywords only - show during loading or when we have data
        if (activeItem?.type === 'keyword' && (loadingStates.definition || keywordDefinition)) {
            components.push({
                id: 'definition',
                title: 'Textual Context',
                component: 'DefinitionSection',
                ...COMPONENT_HEIGHTS.definition
            });
        }

        // Add News second
        if (relatedNews.length > 0 && !errorStates.news && !loadingStates.news) {
            components.push({ 
                id: 'news', 
                title: 'Recent News', 
                component: 'NewsSection', 
                ...COMPONENT_HEIGHTS.news 
            });
        }

        // Add related first (loads faster)
        if ((relatedKeywords.length > 0 || relatedCompanies.length > 0) && !errorStates.related && !loadingStates.related) {
            components.push({ 
                id: 'related', 
                title: 'Related Entities', 
                component: 'RelatedSection', 
                ...COMPONENT_HEIGHTS.related 
            });
        }

        // Company Overview (company only) - shows logo, profile, and key metrics
        if (activeItem?.type === 'company') {
            components.push({ 
                id: 'chart', 
                title: 'Multi-Source Chart', 
                component: 'AnalysisChartSection', 
                ...COMPONENT_HEIGHTS.chart 
            });
        }


        // Only add expectations if we have a company selected AND data loaded successfully
        if (activeItem?.type === 'company' && !errorStates.expectations && !loadingStates.expectations && analystExpectations) {
            components.push({ 
                id: 'expectations', 
                title: 'Analyst Expectations', 
                component: 'ExpectationsSection', 
                ...COMPONENT_HEIGHTS.expectations 
            });
        }

        // Revenue Segmentation (company only)
        if (activeItem?.type === 'company' && !errorStates.revenue && !loadingStates.revenue && (revenueSegments?.rows?.length > 0)) {
            components.push({
                id: 'revenue',
                title: 'Revenue Segmentation',
                component: 'RevenueSection',
                ...COMPONENT_HEIGHTS.revenue
            });
        }

        // Only add sentiment if we have selected tickers AND data loaded successfully
        const isAnySentimentLoading = typeof loadingStates.sentiment === 'object'
            ? Object.values(loadingStates.sentiment).some(Boolean)
            : Boolean(loadingStates.sentiment);
        const hasAnySentimentError = typeof errorStates.sentiment === 'object'
            ? Object.values(errorStates.sentiment).some(Boolean)
            : Boolean(errorStates.sentiment);

        if (selectedTickers.size > 0 && !hasAnySentimentError && !isAnySentimentLoading && Object.keys(sentimentData).length > 0) {
            components.push({ 
                id: 'sentiment', 
                title: 'Street Sentiment Analysis', 
                component: 'SentimentSection', 
                ...COMPONENT_HEIGHTS.sentiment 
            });
        }

        console.log('🔧 Grid components order:', components.map(c => c.id));
        console.log('🔧 Definition state:', { 
            loading: loadingStates.definition, 
            error: errorStates.definition, 
            hasDefinition: !!keywordDefinition,
            activeItemType: activeItem?.type 
        });
        return components;
    }, [selectedTickers, activeItem, relatedKeywords, relatedCompanies, relatedNews, keywordDefinition, analystExpectations, sentimentData, errorStates, loadingStates]);

    // Removed automatic height measurement - using fixed heights now

    // Force layout reset in development when component structure changes
    useEffect(() => {
        if (process.env.NODE_ENV === 'development' && gridComponents) {
            setLayouts(null); // Reset layouts when gridComponents change
        }
    }, [gridComponents.map(c => `${c.id}-${c.defaultH}`).join(',')]);

    // Generate layout for grid components
    const generateLayout = useCallback((items, cols, breakpoint) => {
        const existingLayout = layouts?.[breakpoint] || [];
        
        return items.map((item, index) => {
            const existing = existingLayout.find(l => l.i === item.id);
            if (existing) return existing;

            // Horizontal positioning logic - fill left to right first
            const row = Math.floor(index / cols);
            const col = index % cols;
            
            return {
                i: item.id,
                x: col,
                y: row * 5, // Fixed row height for consistent spacing
                w: 1,
                h: item.defaultH, // Use fixed default height
                minW: 1,
                maxW: cols,
                minH: item.minH,
                maxH: 8 // Allow user to resize up to 8 units
            };
        });
    }, [layouts]);

    const gridLayouts = useMemo(() => ({
        lg: generateLayout(gridComponents, 1, 'lg'),   // 1 column for sidebar
        md: generateLayout(gridComponents, 1, 'md'),   // 1 column for sidebar
        sm: generateLayout(gridComponents, 1, 'sm'),   // 1 column on small screens
        xs: generateLayout(gridComponents, 1, 'xs')    // 1 column on extra small screens
    }), [gridComponents, generateLayout]);

    const handleLayoutChange = useCallback((layout, newLayouts) => {
        setIsLayoutUpdating(true);
        setLayouts(newLayouts);
        setTimeout(() => setIsLayoutUpdating(false), 300);
    }, []);

    // Fetch associated keywords for companies (different from related items for keywords)
    const fetchAssociatedKeywords = useCallback(async (symbol) => {
        if (!symbol) return;
        
        try {
            const response = await fetch(`/company_associated_keywords?symbol=${encodeURIComponent(symbol)}`);
            if (!response.ok) throw new Error('Failed to fetch associated keywords');
            const data = await response.json();
            
            if (data.success) {
                // Store in the same format as related items
                const relatedData = {
                    related_keywords: data.associated_keywords || [],
                    related_companies: (data.related_companies || []).map(comp => ({
                        company_id: comp.id,
                        symbol: comp.symbol || comp.name,
                        name: comp.name
                    }))
                };
                
                // Use the context's setData method to cache this
                // This is a bit of a hack since we're using the related_items cache for company keywords
                const cacheKey = `company_keywords_${symbol}`;
                // We'll need to handle this in the context or create a separate cache entry
                return relatedData;
            }
        } catch (error) {
            console.error('Error fetching associated keywords:', error);
            throw error;
        }
    }, []);

    // Helper function to strip exchange suffix from ticker symbols for news queries
    const stripExchangeSuffix = useCallback((ticker) => {
        if (!ticker || typeof ticker !== 'string') return ticker;
        // Remove exchange suffix (e.g., SARKY.IS -> SARKY)
        return ticker.split('.')[0];
    }, []);

    // --- Chart Data Handling ---
    const handleDataTypeSelect = useCallback(async (dataType, keyword) => {
        if (!chartRef.current || !chartRef.current.handleDataTypeSelect) {
            console.warn('Chart ref not ready for data loading');
            return Promise.reject(new Error('Chart not ready'));
        }
        
        // For news data, strip exchange suffix from ticker symbols
        const searchTerm = dataType === 'news' ? stripExchangeSuffix(keyword) : keyword;
        
        console.log(`🔄 InsightsSidebar: Loading ${dataType} data for: ${keyword}${dataType === 'news' && searchTerm !== keyword ? ` (using: ${searchTerm})` : ''}`);
        try {
            await chartRef.current.handleDataTypeSelect(dataType, searchTerm);
            console.log(`✅ InsightsSidebar: Successfully loaded ${dataType} data for: ${searchTerm}`);
        } catch (error) {
            console.error(`❌ InsightsSidebar: Error loading ${dataType} data for ${searchTerm}:`, error);
            setChartError(`Failed to load ${dataType} data for ${searchTerm}`);
            throw error;
        }
    }, [stripExchangeSuffix]);

    // --- Event Handlers ---
    const handleCompanySelectEvent = useCallback((event) => {
        const company = event.detail;
        if (!company || company.isReset) {
            setActiveItem(null);
            return;
        }
        
        const newItem = { type: 'company', data: company };
        setActiveItem(newItem);
        setUserClosed(false);
    }, [setActiveItem, setUserClosed]);

    const handleKeywordSelectEvent = useCallback((event) => {
        const keyword = event.detail;
        console.log("🔍 InsightsSidebar: Received keywordSelected event:", keyword);
        console.log("🔍 InsightsSidebar: Keyword name:", keyword?.keyword_name);
        
        if (!keyword || keyword.isReset) {
            console.log("🔍 InsightsSidebar: Resetting active item");
            setActiveItem(null);
            return;
        }
        
        const newItem = { type: 'keyword', data: keyword };
        console.log("🔍 InsightsSidebar: Setting new active item:", newItem);
        setActiveItem(newItem);
        setUserClosed(false);
    }, [setActiveItem, setUserClosed]);

    const handleResetEvent = useCallback(() => {
        console.log('🔄 InsightsSidebar: Handling reset event');
        
        // Clear chart when resetting
        if (chartRef.current && chartRef.current.clearChart) {
            console.log('🧹 InsightsSidebar: Clearing chart on reset');
            chartRef.current.clearChart();
        }
        
        setActiveItem(null);
        setUserClosed(false);
        lastProcessedRef.current = null; // Clear the last processed item
    }, [setActiveItem, setUserClosed]);

    // --- Sidebar Toggle Handlers (only when not stacked) ---
    const toggleSidebar = useCallback(() => {
        if (!isStacked) {
            const newIsOpen = !isOpen;
            setIsOpen(newIsOpen);
            setUserClosed(!newIsOpen);
            setInternalIsOpen(newIsOpen);
        }
    }, [isStacked, isOpen, setIsOpen, setUserClosed]);

    const closeSidebar = useCallback(() => {
        if (!isStacked) {
            setUserClosed(true);
            setIsOpen(false);
            setInternalIsOpen(false);
        } else if (onParentClose) {
            onParentClose();
        }
    }, [isStacked, onParentClose, setIsOpen, setUserClosed]);

    // Use parent's open state when stacked, otherwise use context state
    const isOpen = isStacked ? parentIsOpen : (contextIsOpen || internalIsOpen);

    // Auto-open sidebar when an item is selected (only if user preferences allow)
    useEffect(() => {
        if (!isStacked && activeItem && !isOpen && !hasUserClosed && userPreferences.autoExpand) {
            setIsOpen(true);
        }
    }, [activeItem, isOpen, isStacked, hasUserClosed, userPreferences.autoExpand, setIsOpen]);

    // Keyboard support for closing sidebar (only when not stacked)
    useEffect(() => {
        if (!isStacked) {
            const handleKeyDown = (event) => {
                if (event.key === 'Escape' && isOpen) {
                    closeSidebar();
                }
            };

            if (isOpen) {
                document.addEventListener('keydown', handleKeyDown);
                return () => document.removeEventListener('keydown', handleKeyDown);
            }
        }
    }, [isOpen, closeSidebar, isStacked]);

    // --- Event Listeners ---
    useEffect(() => {
        window.addEventListener('companySelected', handleCompanySelectEvent);
        window.addEventListener('keywordSelected', handleKeywordSelectEvent);
        window.addEventListener('resetUI', handleResetEvent);

        return () => {
            window.removeEventListener('companySelected', handleCompanySelectEvent);
            window.removeEventListener('keywordSelected', handleKeywordSelectEvent);
            window.removeEventListener('resetUI', handleResetEvent);
        };
    }, [handleCompanySelectEvent, handleKeywordSelectEvent, handleResetEvent]);

    // --- Data Fetching Effect ---
    useEffect(() => {
        // For stacked sidebars, always process data (parent controls visibility)
        // For non-stacked, only process if we have an activeItem (let auto-expand handle opening)
        if (!isStacked && !activeItem) {
            console.log('🚫 No active item - skipping data fetch');
            return;
        }

        if (!activeItem) {
            // Clear chart if available
            if (chartRef.current && chartRef.current.clearChart) {
                chartRef.current.clearChart();
            }
            lastProcessedRef.current = null;
            return;
        }

        // Create a unique key for the current item to prevent duplicate processing
        const itemKey = activeItem.type === 'company' 
            ? `company-${activeItem.data.symbol}` 
            : `keyword-${activeItem.data.keyword_name}`;
        
        // Skip if we just processed this exact item
        if (lastProcessedRef.current === itemKey) {
            console.log('🔄 Skipping duplicate data fetch for:', itemKey);
            return;
        }

        console.log('🔄 Processing data fetch for:', itemKey, {
            isOpen,
            isStacked,
            activeItemType: activeItem?.type,
            previousKey: lastProcessedRef.current
        });
        
        // Clear chart before loading new data
        if (chartRef.current && chartRef.current.clearChart) {
            console.log('🧹 InsightsSidebar: Clearing chart before loading new data');
            chartRef.current.clearChart();
        }
        
        lastProcessedRef.current = itemKey;
        setSelectedTickers(new Set());
        setChartError(null);

        if (activeItem.type === 'company') {
            const symbol = activeItem.data.symbol;
            const searchTerm = activeItem.data.symbol || activeItem.data.name;
            
            if (symbol) {
                console.log(`🔄 Loading data for company: ${symbol}`);
                
                // Fetch company data using context methods (with caching)
                fetchCompanyProfile(symbol).catch(console.error);
                fetchCompanyLogo(symbol).catch(console.error);
                fetchAnalystExpectations(symbol).catch(console.error);
                fetchRevenueSegmentation(symbol).catch(console.error);
                
                // Fetch associated keywords (company-specific related items)
                fetchAssociatedKeywords(symbol).then(relatedData => {
                    // Store the data in context cache manually for now
                    // TODO: Add proper support for company keywords in context
                    console.log('Associated keywords loaded:', relatedData);
                }).catch(console.error);
                
                // Load news and price data for the company in the multi-source chart
                setTimeout(async () => {
                    console.log(`🎯 InsightsSidebar: Loading chart data for company: ${symbol}`);
                    
                    // Load both news and price data in parallel
                    const dataLoadPromises = [
                        handleDataTypeSelect('news', symbol)
                            .then(() => console.log(`✅ News data loaded successfully for: ${symbol}`))
                            .catch(error => console.error(`❌ Failed to load news for ${symbol}:`, error)),
                        
                        handleDataTypeSelect('price', symbol)
                            .then(() => console.log(`✅ Price data loaded successfully for: ${symbol}`))
                            .catch(error => console.error(`❌ Failed to load price for ${symbol}:`, error))
                    ];
                    
                    console.log(`📊 Loading news and price data for company: ${symbol}`);
                    await Promise.allSettled(dataLoadPromises);
                    console.log(`🎯 Completed loading all chart data for company: ${symbol}`);
                }, 500);
            }
        } else if (activeItem.type === 'keyword') {
            const keyword = activeItem.data.keyword_name;
            console.log(`🔍 InsightsSidebar: Processing keyword data:`, activeItem.data);
            console.log(`🔍 InsightsSidebar: Extracted keyword name: "${keyword}"`);
            
            if (keyword) {
                console.log(`🔄 Loading data for keyword: ${keyword}`);
                
                // Fetch keyword data using context methods (with caching)
                fetchRelatedItems(keyword).catch(console.error);
                fetchRelatedNews(keyword).catch(console.error);
                fetchKeywordDefinition(keyword).catch(console.error);
                
                // Load multiple data sources automatically for better insights
                setTimeout(async () => {
                    console.log(`🎯 InsightsSidebar: Loading chart data for keyword: ${keyword}`);
                    
                    // Load all data types in parallel with individual error handling
                    const dataLoadPromises = [
                        handleDataTypeSelect('search', keyword)
                            .then(() => console.log(`✅ Search data loaded successfully for: ${keyword}`))
                            .catch(error => console.error(`❌ Failed to load search for ${keyword}:`, error)),
                        
                        handleDataTypeSelect('news', keyword)
                            .then(() => console.log(`✅ News data loaded successfully for: ${keyword}`))
                            .catch(error => console.error(`❌ Failed to load news for ${keyword}:`, error)),
                        
                        handleDataTypeSelect('price', keyword)
                            .then(() => console.log(`✅ Price data loaded successfully for: ${keyword}`))
                            .catch(error => console.error(`❌ Failed to load price for ${keyword}:`, error))
                    ];
                    
                    console.log(`📊 Loading search, news, and price data for: ${keyword}`);
                    await Promise.allSettled(dataLoadPromises);
                    console.log(`🎯 Completed loading all chart data for: ${keyword}`);
                }, 500);
            }
        }
    }, [activeItem, isStacked, fetchCompanyProfile, fetchCompanyLogo, fetchAnalystExpectations, fetchRevenueSegmentation, fetchRelatedItems, fetchRelatedNews, fetchKeywordDefinition, fetchAssociatedKeywords, handleDataTypeSelect]);

    const handleTickerSelect = useCallback(async (ticker, isPrimary = false) => {
        console.log(`Handling ticker select: ${ticker}, isPrimary: ${isPrimary}`);
        
        const newSelected = new Set(selectedTickers);
        
        if (newSelected.has(ticker) && !isPrimary) {
            console.log(`Removing ticker: ${ticker}`);
            newSelected.delete(ticker);
            setSentimentData(prev => {
                const newState = {...prev};
                delete newState[ticker];
                return newState;
            });
            try {
                await handleDataTypeSelect('price', ticker);
                await handleDataTypeSelect('news', ticker);
            } catch (error) {
                console.warn('Error removing data from chart:', error);
            }
        } else if (!newSelected.has(ticker)) {
            console.log(`Adding ticker: ${ticker}`);
            newSelected.add(ticker);
            
            const promises = [
                handleDataTypeSelect('price', ticker).catch(error => console.warn(`Failed to load price for ${ticker}:`, error)),
                handleDataTypeSelect('news', ticker).catch(error => console.warn(`Failed to load news for ${ticker}:`, error)),
                fetchAnalystSentiment(ticker).catch(error => console.warn(`Failed to load sentiment for ${ticker}:`, error))
            ];
            
            await Promise.allSettled(promises);
        }
        
        setSelectedTickers(newSelected);
    }, [selectedTickers, handleDataTypeSelect, fetchAnalystSentiment]);

    // --- Click Handlers ---
    const handleRelatedKeywordClick = useCallback((keyword) => {
        window.dispatchEvent(new CustomEvent('triggerKeywordSearch', {
            detail: { keyword: keyword.keyword_name, keywordObject: keyword }
        }));
        window.dispatchEvent(new CustomEvent('keywordSelected', { detail: keyword }));
    }, []);

    const handleRelatedCompanyClick = useCallback((company) => {
        handleTickerSelect(company.symbol);
    }, [handleTickerSelect]);

    // --- Component Helpers ---
    const formatPrice = (price) => {
        if (!price) return 'N/A';
        return `$${parseFloat(price).toFixed(2)}`;
    };

    const formatMarketCap = (marketCap) => {
        if (!marketCap) return 'N/A';
        const value = parseFloat(marketCap);
        if (value >= 1e12) return `$${(value / 1e12).toFixed(1)}T`;
        if (value >= 1e9) return `$${(value / 1e9).toFixed(1)}B`;
        if (value >= 1e6) return `$${(value / 1e6).toFixed(1)}M`;
        return `$${value.toFixed(0)}`;
    };

    // --- Render Components ---
    const HeroCard = () => {
        if (!activeItem) {
            return (
                <div className="hero-card hero-card-empty">
                    <div className="hero-empty-state">
                        <p>Select a keyword or company to view insights</p>
                    </div>
                </div>
            );
        }

        // For companies, don't show hero card since company info will be shown under Insights header
        if (activeItem.type === 'company') {
            return null;
        }

        // For keywords, show the compact keyword card
        const keyword = activeItem.data;
        return (
            <div className="hero-card-compact hero-card-keyword-compact">
                <div className="hero-content-horizontal">
                    <div className="hero-keyword-icon-compact">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                            <path d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"/>
                        </svg>
                    </div>
                    <div className="hero-keyword-info-compact">
                        <div className="hero-keyword-name-compact">{keyword.keyword_name}</div>
                        <div className="hero-keyword-type-compact">Search Keyword</div>
                    </div>
                </div>
            </div>
        );
    };



    // Grid Component Renderers
    const renderGridComponent = (componentId) => {
        switch (componentId) {
            case 'chart':
                return (
                    <div className="grid-card-content" style={{ height: '100%' }}>
                        <Suspense fallback={<div className="loading-spinner-container"><div className="loading-spinner"></div></div>}>
                            <TrendChart
                                ref={chartRef}
                                id="insights-sidebar-chart"
                                initialDatasets={[]}
                                isMultiChart={true}
                                className="awareness-chart"
                            />
                        </Suspense>
            </div>
                );

            case 'overview':
                if (!activeItem || activeItem.type !== 'company') return null;
                
                const company = activeItem.data;
                const hasLogo = companyLogo?.logoUrl;
                const hasProfile = companyProfile;
                const isLoading = loadingStates.profile || loadingStates.logo;
                
                return (
                    <div className="grid-card-content">
                        {isLoading ? (
                            <div className="loading-spinner-container">
                                <div className="loading-spinner"></div>
                            </div>
                        ) : (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', padding: '0.5rem 0' }}>
                                {/* Header with logo, basic info, and website button */}
                                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', paddingBottom: '1rem', borderBottom: '1px solid rgba(75, 85, 99, 0.2)' }}>
                                    {hasLogo && (
                                        <div style={{ width: '48px', height: '48px', borderRadius: '8px', overflow: 'hidden', background: 'rgba(255, 255, 255, 0.05)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                            <img 
                                                key={`overview-logo-${company.symbol}-${stableLogoUrl}`}
                                                src={stableLogoUrl} 
                                                alt={`${company.symbol} logo`}
                                                style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                                                loading="lazy"
                                                onLoad={() => {
                                                    setLoadedImages(prev => new Set([...prev, stableLogoUrl]));
                                                    console.log(`✅ Overview logo loaded for ${company.symbol}`);
                                                }}
                                                onError={(e) => {
                                                    console.log(`🚫 Overview logo failed for ${company.symbol}: ${stableLogoUrl}`);
                                                    setFailedLogos(prev => new Set([...prev, company.symbol]));
                                                    // Hide the entire logo container on error
                                                    e.target.parentElement.style.display = 'none';
                                                }}
                                            />
                                        </div>
                                    )}
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        <div style={{ fontSize: '1.25rem', fontWeight: 600, color: '#ffffff', marginBottom: '0.25rem' }}>
                                            {company.symbol}
                                        </div>
                                        <div style={{ fontSize: '0.875rem', color: 'rgba(156, 163, 175, 0.8)' }}>
                                            {hasProfile ? companyProfile.companyName : (company.name || company.companyname || 'Company Name')}
                                        </div>
                                        {hasProfile && companyProfile.exchange && (
                                            <div style={{ fontSize: '0.75rem', color: 'rgba(156, 163, 175, 0.6)', marginTop: '0.25rem' }}>
                                                {companyProfile.exchange} • {companyProfile.country || 'N/A'}
                                            </div>
                                        )}
                                    </div>
                                    
                                    {/* Website button in header */}
                                    {hasProfile && companyProfile.website && (
                                        <a 
                                            href={companyProfile.website} 
                                            target="_blank" 
                                            rel="noopener noreferrer"
                                            style={{ 
                                                display: 'flex', 
                                                alignItems: 'center', 
                                                justifyContent: 'center',
                                                width: '36px',
                                                height: '36px',
                                                background: 'rgba(255, 255, 255, 0.08)',
                                                border: '1px solid rgba(255, 255, 255, 0.15)',
                                                borderRadius: '8px',
                                                color: 'rgba(255, 255, 255, 0.7)',
                                                textDecoration: 'none',
                                                transition: 'all 0.2s ease',
                                                flexShrink: 0
                                            }}
                                            title="Visit Company Website"
                                            onMouseEnter={(e) => {
                                                e.target.style.background = 'rgba(255, 255, 255, 0.12)';
                                                e.target.style.borderColor = 'rgba(255, 255, 255, 0.25)';
                                                e.target.style.color = 'rgba(255, 255, 255, 0.9)';
                                            }}
                                            onMouseLeave={(e) => {
                                                e.target.style.background = 'rgba(255, 255, 255, 0.08)';
                                                e.target.style.borderColor = 'rgba(255, 255, 255, 0.15)';
                                                e.target.style.color = 'rgba(255, 255, 255, 0.7)';
                                            }}
                                        >
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6M15 3h6v6M10 14L21 3"/>
                                            </svg>
                                        </a>
                                    )}
                                </div>

                                {/* Key metrics grid */}
                                {hasProfile && (
                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '0.75rem' }}>
                                        {companyProfile.price && (
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                                                <div style={{ fontSize: '0.75rem', color: 'rgba(156, 163, 175, 0.6)', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 500 }}>
                                                    Price
                                                </div>
                                                <div style={{ fontSize: '0.875rem', color: '#ffffff', fontWeight: 600 }}>
                                                    {formatPrice(companyProfile.price)}
                                                </div>
                                            </div>
                                        )}
                                        {companyProfile.marketCap && (
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                                                <div style={{ fontSize: '0.75rem', color: 'rgba(156, 163, 175, 0.6)', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 500 }}>
                                                    Market Cap
                                                </div>
                                                <div style={{ fontSize: '0.875rem', color: '#ffffff', fontWeight: 600 }}>
                                                    {formatMarketCap(companyProfile.marketCap)}
                                                </div>
                                            </div>
                                        )}
                                        {companyProfile.industry && (
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                                                <div style={{ fontSize: '0.75rem', color: 'rgba(156, 163, 175, 0.6)', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 500 }}>
                                                    Industry
                                                </div>
                                                <div style={{ fontSize: '0.875rem', color: '#ffffff', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                    {companyProfile.industry}
                                                </div>
                                            </div>
                                        )}
                                        {companyProfile.fullTimeEmployees && (
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                                                <div style={{ fontSize: '0.75rem', color: 'rgba(156, 163, 175, 0.6)', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 500 }}>
                                                    Employees
                                                </div>
                                                <div style={{ fontSize: '0.875rem', color: '#ffffff', fontWeight: 600 }}>
                                                    {companyProfile.fullTimeEmployees.toLocaleString()}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* Description */}
                                {hasProfile && companyProfile.description && (
                                    <div style={{ borderTop: '1px solid rgba(75, 85, 99, 0.15)', paddingTop: '1rem', flex: 1, minHeight: 0 }}>
                                        {/* Scrollable description */}
                                        <div className="ov-desc-scroll" style={{ 
                                            fontSize: '0.875rem', 
                                            color: 'rgba(209, 213, 219, 0.9)', 
                                            lineHeight: 1.6, 
                                            overflowY: 'auto',
                                            paddingRight: '0.5rem',
                                            maxHeight: '120px'
                                        }}>
                                            {companyProfile.description}
                                        </div>
                                    </div>
                                )}

                                {/* Error state */}
                                {errorStates.profile && (
                                    <div style={{ padding: '1rem', textAlign: 'center', color: 'rgba(239, 68, 68, 0.7)', fontSize: '0.875rem' }}>
                                        Failed to load company profile
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                );

            case 'related':
                return (
                    <div className="grid-card-content">
                        {relatedKeywords.length > 0 && (
                            <div className="related-group">
                                <h4>Keywords</h4>
                                <div className="related-items">
                                    {relatedKeywords.slice(0, 8).map((keyword, index) => (
                                        <button
                                            key={index}
                                            onClick={() => handleRelatedKeywordClick(keyword)}
                                            className="related-item related-keyword"
                                        >
                                            <span className="item-label">{keyword.keyword_name}</span>
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}
                        
                        {relatedCompanies.length > 0 && (
                            <div className="related-group">
                                <h4>Companies</h4>
                                <div className="related-items">
                                    {relatedCompanies.slice(0, 8).map((company) => (
                                        <button
                                            key={company.company_id}
                                            onClick={() => handleRelatedCompanyClick(company)}
                                            className={`related-item related-company ${selectedTickers.has(company.symbol) ? 'selected' : ''}`}
                                        >
                                            <span className="item-label">{company.symbol}</span>
                                            <span className="add-remove-icon">
                                                {selectedTickers.has(company.symbol) ? '−' : '+'}
                                            </span>
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}

                        {errorStates.related && (
                            <div className="error-state">
                                <p>Failed to load related items</p>
                            </div>
                        )}

                        {!loadingStates.related && relatedKeywords.length === 0 && relatedCompanies.length === 0 && (
                            <div className="empty-state">
                                <p>No related items found</p>
                            </div>
                        )}
                    </div>
                );

            case 'sentiment':
                if (selectedTickers.size === 0) return null;
                return (
                    <div className="grid-card-content">
                        {Array.from(selectedTickers).map(ticker => {
                            const sentiment = sentimentData[ticker];
                            const isLoading = loadingStates.sentiment?.[ticker];
                            const hasError = errorStates.sentiment?.[ticker];

                            if (isLoading) {
                                return (
                                    <div key={ticker} style={{ marginBottom: '16px' }}>
                                        <div style={{ 
                                            fontSize: '0.8rem', 
                                            color: 'rgba(255, 255, 255, 0.6)',
                                            marginBottom: '8px' 
                                        }}>
                                            {ticker} • Loading sentiment...
                                        </div>
                                        <div style={{
                                            height: '4px',
                                            background: 'rgba(255, 255, 255, 0.1)',
                                            borderRadius: '2px',
                                            overflow: 'hidden'
                                        }}>
                                            <div style={{
                                                height: '100%',
                                                background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent)',
                                                animation: 'shimmer 1.5s infinite'
                                            }} />
            </div>
        </div>
    );
                            }
                            
                            if (hasError) {
                                return <div key={ticker} className="error-state">Could not load sentiment for {ticker}.</div>;
                            }
                            
                            if (!sentiment) return null;
                            
                            // Handle no data case
                            if (sentiment.error === 'no_analyst_data') {
                                return (
                                    <div key={ticker} style={{ marginBottom: '20px' }}>
                                        <div style={{ 
                                            display: 'flex', 
                                            justifyContent: 'space-between', 
                                            alignItems: 'center',
                                            marginBottom: '12px' 
                                        }}>
                                            <div style={{ 
                                                fontSize: '0.9rem', 
                                                color: 'white',
                                                fontWeight: '500' 
                                            }}>
                                                {ticker} • ${sentiment.currentPrice?.toFixed(2)}
            </div>
                                            <div style={{
                                                fontSize: '0.8rem',
                                                color: sentiment.sentimentColor,
                                                fontWeight: '500'
                                            }}>
                                                {sentiment.sentimentLabel}
                                    </div>
                                </div>
                                        <div style={{
                                            padding: '12px',
                                            background: 'rgba(107, 114, 128, 0.1)',
                                            border: '1px solid rgba(107, 114, 128, 0.2)',
                                            borderRadius: '6px',
                                            fontSize: '0.7rem',
                                            color: 'rgba(255, 255, 255, 0.6)',
                                            lineHeight: '1.4'
                                        }}>
                                            📊 {sentiment.message}
                            </div>
                    </div>
                                );
                            }
                            
                            return (
                                <div key={ticker} style={{ marginBottom: '20px' }}>
                                    {/* Header */}
                                    <div style={{ 
                                        display: 'flex', 
                                        justifyContent: 'space-between', 
                                        alignItems: 'center',
                                        marginBottom: '12px' 
                                    }}>
                                        <div style={{ 
                                            fontSize: '0.9rem', 
                                            color: 'white',
                                            fontWeight: '500' 
                                        }}>
                                            {ticker} • ${sentiment.currentPrice?.toFixed(2)}
                    </div>
                                        <div style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '8px'
                                        }}>
                                            <span style={{
                                                fontSize: '0.8rem',
                                                color: sentiment.sentimentColor,
                                                fontWeight: '500'
                                            }}>
                                                {sentiment.sentimentLabel}
                                            </span>
                                            <span style={{
                                                fontSize: '1rem',
                                                color: sentiment.sentimentColor,
                                                fontWeight: '600'
                                            }}>
                                                {sentiment.overallScore}/100
                                            </span>
            </div>
                                    </div>
                                    
                                    {/* Overall Sentiment Bar */}
                                    <div style={{
                                        height: '8px',
                                        background: 'rgba(255, 255, 255, 0.1)',
                                        borderRadius: '4px',
                                        overflow: 'hidden',
                                        marginBottom: '12px'
                                    }}>
                                        <div style={{
                                            height: '100%',
                                            width: `${sentiment.overallScore}%`,
                                            background: `linear-gradient(90deg, ${sentiment.sentimentColor}, ${sentiment.sentimentColor}cc)`,
                                            borderRadius: '4px',
                                            transition: 'width 0.3s ease'
                                        }} />
                                    </div>
                                    
                                    {/* Component Breakdown */}
                                    <div style={{ 
                                        display: 'grid', 
                                        gridTemplateColumns: 'repeat(3, 1fr)', 
                                        gap: '12px',
                                        fontSize: '0.7rem'
                                    }}>
                                        {/* Price Target */}
                                        <div>
                                            <div style={{ 
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center',
                                                marginBottom: '4px'
                                            }}>
                                                <span style={{ color: 'rgba(255, 255, 255, 0.5)'}}>
                                                    Price Target
                                                </span>
                                                <span style={{ 
                                                    color: sentiment.components.priceTarget.upside >= 0 ? '#4ade80' : '#f87171',
                                                    fontSize: '0.65rem',
                                                    fontWeight: '500'
                                                }}>
                                                    {sentiment.components.priceTarget.upside >= 0 ? 'Upside' : 'Downside'}: {sentiment.components.priceTarget.upside >= 0 ? '+' : ''}
                                                    {Math.abs(sentiment.components.priceTarget.upside).toFixed(1)}%
                                                </span>
                                            </div>
                                            
                                            {/* Analyst Range Bar */}
                                            <div style={{ position: 'relative', marginTop: '4px' }}>
                                                {(() => {
                                                    const low = sentiment.components.priceTarget.targetLow;
                                                    const high = sentiment.components.priceTarget.targetHigh;
                                                    const current = sentiment.currentPrice;
                                                    const consensus = sentiment.components.priceTarget.targetPrice;
                                                    
                                                    if (low === null || high === null || current === null || consensus === null || high <= low) {
                                                        return (
                                                            <div style={{ height: '10px', fontSize: '0.65rem', color: 'rgba(255, 255, 255, 0.4)', textAlign: 'center' }}>
                                                                Not enough data for range view
        </div>
    );
                                                    }

                                                    const range = high - low;
                                                    const currentPos = Math.max(0, Math.min(100, ((current - low) / range) * 100));
                                                    const consensusPos = Math.max(0, Math.min(100, ((consensus - low) / range) * 100));

        return (
                                                        <>
                                                            {/* Top Labels (Low and High prices) */}
                                                            <div style={{
                                                                display: 'flex',
                                                                justifyContent: 'space-between',
                                                                fontSize: '0.65rem',
                                                                color: 'rgba(255, 255, 255, 0.6)',
                                                                marginBottom: '2px'
                                                            }}>
                                                                <span>${low.toFixed(2)}</span>
                                                                <span>${high.toFixed(2)}</span>
                </div>

                                                            {/* The main bar container */}
                                                            <div style={{ position: 'relative', height: '8px' }}>
                                                                <div style={{
                                                                    height: '3px',
                                                                    background: 'rgba(255, 255, 255, 0.2)',
                                                                    borderRadius: '2px',
                                                                    position: 'absolute',
                                                                    width: '100%',
                                                                    top: '2.5px'
                                                                }} />
                                                                
                                                                {/* Current Price Marker (with hover title) */}
                                                                <div style={{
                                                                    position: 'absolute',
                                                                    left: `${currentPos}%`,
                                                                    top: 0,
                                                                    height: '8px',
                                                                    width: '8px',
                                                                    background: 'white',
                                                                    borderRadius: '50%',
                                                                    transform: 'translateX(-50%)',
                                                                    zIndex: 2,
                                                                    boxShadow: '0 0 5px rgba(0,0,0,0.5)'
                                                                }} title={`Current: $${current.toFixed(2)}`} />

                                                                {/* Consensus Target Marker (with hover title) */}
                                                                <div style={{
                                                                    position: 'absolute',
                                                                    left: `${consensusPos}%`,
                                                                    top: 0,
                                                                    height: '8px',
                                                                    width: '2px',
                                                                    background: '#60a5fa',
                                                                    borderRadius: '1px',
                                                                    transform: 'translateX(-50%)',
                                                                    zIndex: 1
                                                                }} title={`Consensus: $${consensus.toFixed(2)}`} />
                                                            </div>
                                                        </>
                                                    );
                                                })()}
                                            </div>
                                        </div>

                                        {/* Analyst Ratings */}
                                        <div>
                                            <div style={{ 
                                                color: 'rgba(255, 255, 255, 0.5)',
                                                marginBottom: '4px'
                                            }}>
                                                Analyst Ratings
                                            </div>
                                            <div style={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center',
                                                marginBottom: '2px'
                                            }}>
                                                <span style={{ 
                                                    color: '#dc2626',
                                                    fontSize: '0.65rem'
                                                }}>
                                                    {sentiment.components.analystRatings.breakdown['Sell'] + sentiment.components.analystRatings.breakdown['Strong Sell']} Sell
                                                </span>
                                                <span style={{ 
                                                    color: '#fbbf24',
                                                    fontSize: '0.65rem'
                                                }}>
                                                    {sentiment.components.analystRatings.breakdown['Hold']} Hold
                                                </span>
                                                <span style={{ 
                                                    color: '#4ade80',
                                                    fontSize: '0.65rem'
                                                }}>
                                                    {sentiment.components.analystRatings.breakdown['Buy'] + sentiment.components.analystRatings.breakdown['Strong Buy']} Buy
                                                </span>
                                            </div>
                                            <div style={{
                                                height: '3px',
                                                background: 'rgba(255, 255, 255, 0.1)',
                                                borderRadius: '2px',
                                                overflow: 'hidden',
                                                display: 'flex'
                                            }}>
                                                {(() => {
                                                    const totalRatings = Object.values(sentiment.components.analystRatings.breakdown).reduce((a, b) => a + b, 0);
                                                    const sellCount = sentiment.components.analystRatings.breakdown['Sell'] + sentiment.components.analystRatings.breakdown['Strong Sell'];
                                                    const holdCount = sentiment.components.analystRatings.breakdown['Hold'];
                                                    const buyCount = sentiment.components.analystRatings.breakdown['Buy'] + sentiment.components.analystRatings.breakdown['Strong Buy'];
                                                    
                                                    return (
                                                        <>
                                                            {/* Sell ratings (dark red) on the left */}
                                                            <div style={{
                                                                height: '100%',
                                                                width: `${(sellCount / Math.max(1, totalRatings)) * 100}%`,
                                                                background: '#dc2626'
                                                            }} />
                                                            {/* Hold ratings (yellow) in the middle */}
                                                            <div style={{
                                                                height: '100%',
                                                                width: `${(holdCount / Math.max(1, totalRatings)) * 100}%`,
                                                                background: '#fbbf24'
                                                            }} />
                                                            {/* Buy ratings (green) on the right */}
                                                            <div style={{
                                                                height: '100%',
                                                                width: `${(buyCount / Math.max(1, totalRatings)) * 100}%`,
                                                                background: '#4ade80'
                                                            }} />
                                                        </>
                                                    );
                                                })()}
                                            </div>
                                        </div>
                                        
                                        {/* Up/Downgrades */}
                                        <div>
                                            <div style={{ 
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center',
                                                marginBottom: '4px'
                                            }}>
                                                <span style={{ color: 'rgba(255, 255, 255, 0.5)'}}>
                                                    Up/Downgrades
                                                </span>
                                                <span style={{ 
                                                    color: 'rgba(255, 255, 255, 0.4)', 
                                                    fontSize: '0.6rem',
                                                    textTransform: 'uppercase'
                                                }}>
                                                    Last 3 Months
                                                </span>
                                            </div>
                                            <div style={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center',
                                                marginBottom: '2px'
                                            }}>
                                                <span style={{ 
                                                    color: '#f87171',
                                                    fontSize: '0.65rem'
                                                }}>
                                                    {sentiment.components.momentum.downgrades} ↓
                                                </span>
                                                <span style={{ 
                                                    color: 'rgba(255, 255, 255, 0.8)',
                                                    fontSize: '0.65rem'
                                                }}>
                                                    {sentiment.components.momentum.upgrades + sentiment.components.momentum.downgrades} actions
                                                </span>
                                                <span style={{ 
                                                    color: '#4ade80',
                                                    fontSize: '0.65rem'
                                                }}>
                                                    {sentiment.components.momentum.upgrades} ↑
                                                </span>
                                            </div>
                                            <div style={{
                                                height: '3px',
                                                background: 'rgba(255, 255, 255, 0.1)',
                                                borderRadius: '2px',
                                                overflow: 'hidden',
                                                display: 'flex'
                                            }}>
                                                {/* Downgrades (red) on the left */}
                                                <div style={{
                                                    height: '100%',
                                                    width: `${(sentiment.components.momentum.downgrades / Math.max(1, sentiment.components.momentum.upgrades + sentiment.components.momentum.downgrades)) * 100}%`,
                                                    background: '#f87171'
                                                }} />
                                                {/* Upgrades (green) on the right */}
                                                <div style={{
                                                    height: '100%',
                                                    width: `${(sentiment.components.momentum.upgrades / Math.max(1, sentiment.components.momentum.upgrades + sentiment.components.momentum.downgrades)) * 100}%`,
                                                    background: '#4ade80'
                                                }} />
                                            </div>
                                        </div>
                                    </div>
                                    
                                    {/* Recent Actions Scrollable List */}
                                    {sentiment.components.momentum.recentActions && sentiment.components.momentum.recentActions.length > 0 && (
                                        <div style={{ marginTop: '16px' }}>
                                            <div style={{
                                                fontSize: '0.7rem',
                                                color: 'rgba(255, 255, 255, 0.6)',
                                                marginBottom: '8px',
                                                fontWeight: '500'
                                            }}>
                                                Recent Actions ({sentiment.components.momentum.recentActions.length})
                                            </div>
                                            <div style={{
                                                maxHeight: '200px',
                                                overflowY: 'auto',
                                                background: 'rgba(0, 0, 0, 0.2)',
                                                borderRadius: '6px',
                                                padding: '8px'
                                            }}>
                                                {sentiment.components.momentum.recentActions.map((action, actionIndex) => {
                                                    const actionDate = new Date(action.publishedDate);
                                                    const formattedDate = actionDate.toLocaleDateString('en-US', { 
                                                        month: 'short', 
                                                        day: 'numeric', 
                                                        year: 'numeric' 
                                                    });
                                                    
                                                    // Determine action type and color
                                                    let actionType = action.action;
                                                    let actionColor = '#9ca3af'; // Default gray for hold
                                                    
                                                    if (action.action === 'downgrade') {
                                                        actionColor = '#f87171';
                                                        actionType = 'Downgrade';
                                                    } else if (action.action === 'upgrade') {
                                                        actionColor = '#4ade80';
                                                        actionType = 'Upgrade';
                                                    } else if (action.action === 'hold') {
                                                        actionType = 'Maintained';
                                                    } else if (action.action === 'initialise') {
                                                        actionColor = '#60a5fa';
                                                        actionType = 'Initiated';
                                                    }
                                                    
                                                    // Format grade change
                                                    const gradeChange = action.previousGrade && action.newGrade
                                                        ? `${action.previousGrade} → ${action.newGrade}`
                                                        : action.newGrade || 'N/A';
                                                    
                                                    return (
                                                        <div key={actionIndex} style={{
                                                            marginBottom: actionIndex < sentiment.components.momentum.recentActions.length - 1 ? '12px' : '0',
                                                            padding: '8px',
                                                            background: 'rgba(255, 255, 255, 0.03)',
                                                            borderRadius: '4px',
                                                            border: '1px solid rgba(255, 255, 255, 0.05)'
                                                        }}>
                                                            {/* Header Row - Date and Action */}
                                                            <div style={{
                                                                display: 'flex',
                                                                justifyContent: 'space-between',
                                                                alignItems: 'center',
                                                                marginBottom: '4px'
                                                            }}>
                                                                <span style={{
                                                                    fontSize: '0.65rem',
                                                                    color: 'rgba(255, 255, 255, 0.7)',
                                                                    fontWeight: '500'
                                                                }}>
                                                                    {formattedDate}
                                                                </span>
                                                                <span style={{
                                                                    fontSize: '0.65rem',
                                                                    color: actionColor,
                                                                    fontWeight: '600',
                                                                    textTransform: 'capitalize'
                                                                }}>
                                                                    {actionType}
                                                                </span>
                                                            </div>
                                                            
                                                            {/* Main Content - Firm and Grade */}
                                                            <div style={{
                                                                marginBottom: '4px'
                                                            }}>
                                                                <div style={{
                                                                    fontSize: '0.7rem',
                                                                    color: 'white',
                                                                    fontWeight: '500',
                                                                    marginBottom: '2px'
                                                                }}>
                                                                    {action.gradingCompany}
                                                                </div>
                                                                <div style={{
                                                                    fontSize: '0.65rem',
                                                                    color: 'rgba(255, 255, 255, 0.8)'
                                                                }}>
                                                                    {gradeChange} • ${action.priceWhenPosted?.toFixed(2)}
                                                                </div>
                                                            </div>
                                                            
                                                            {/* News Title (clickable if URL exists) */}
                                                            {action.newsTitle && (
                                                                <div style={{ marginTop: '4px' }}>
                                                                    {action.newsURL ? (
                                                                        <a
                                                                            href={action.newsURL}
                                                                            target="_blank"
                                                                            rel="noopener noreferrer"
                                                                            style={{
                                                                                fontSize: '0.6rem',
                                                                                color: 'rgba(96, 165, 250, 0.8)',
                                                                                textDecoration: 'none',
                                                                                lineHeight: '1.3',
                                                                                display: 'block'
                                                                            }}
                                                                            onMouseOver={(e) => e.target.style.color = 'rgba(96, 165, 250, 1)'}
                                                                            onMouseOut={(e) => e.target.style.color = 'rgba(96, 165, 250, 0.8)'}
                                                                        >
                                                                            {action.newsTitle.slice(0, 80)}{action.newsTitle.length > 80 ? '...' : ''}
                                                                        </a>
                                                                    ) : (
                                                                        <span style={{
                                                                            fontSize: '0.6rem',
                                                                            color: 'rgba(255, 255, 255, 0.5)',
                                                                            lineHeight: '1.3'
                                                                        }}>
                                                                            {action.newsTitle.slice(0, 80)}{action.newsTitle.length > 80 ? '...' : ''}
                                                                        </span>
                                                                    )}
                                                                    {action.newsPublisher && (
                                                                        <span style={{
                                                                            fontSize: '0.55rem',
                                                                            color: 'rgba(255, 255, 255, 0.4)',
                                                                            marginLeft: '8px'
                                                                        }}>
                                                                            via {action.newsPublisher}
                                                                        </span>
                                )}
                            </div>
                                                            )}
                    </div>
                                                    );
                                                })}
                                            </div>
                    </div>
                )}
            </div>
                            );
                        })}
        </div>
    );



            case 'revenue':
                {
                    const rows = revenueSegments?.rows || [];
                    const formatPct = (v) => (v === null || v === undefined) ? '—' : `${Number(v).toFixed(1)}%`;
                    const latestLabel = revenueSegments?.latestLabel;
                    return (
                        <div className="grid-card-content">
                            {rows.length > 0 ? (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '6px', marginBottom: '4px' }}>
                                        <div style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.9)', fontWeight: 600 }}>
                                            Top Segments{latestLabel ? ` • ${latestLabel.slice(0, 4)}` : ''}
                                        </div>
                                        <div style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.5)' }}>
                                            {rows.length} items
                                        </div>
                                    </div>

                                    {rows.map((r, idx) => (
                                        <div key={idx} style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                <div style={{ fontSize: '0.8rem', color: 'white', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '65%' }}>
                                                    {r.name}
                                                </div>
                                                <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                                                    <span style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.8)', fontWeight: 600 }}>
                                                        {r.revenue !== null && r.revenue !== undefined ? formatMarketCap(r.revenue) : '—'}
                                                    </span>
                                                    <span style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.6)' }}>
                                                        {formatPct(r.pct)}
                                                    </span>
                                                </div>
                                            </div>
                                            <div style={{ height: '6px', background: 'rgba(255,255,255,0.09)', borderRadius: '4px', overflow: 'hidden' }}>
                                                {(() => {
                                                    const max = rows[0]?.revenue || 0;
                                                    const width = max > 0 && r.revenue ? Math.max(2, Math.min(100, (r.revenue / max) * 100)) : 0;
                                                    return (
                                                        <div style={{ height: '100%', width: `${width}%`, background: 'linear-gradient(90deg, #16a34a, #16a34acc)', transition: 'width 0.4s ease' }} />
                                                    );
                                                })()}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="empty-state"><p>{errorStates.revenue ? 'Failed to load revenue segmentation' : 'No revenue segmentation available'}</p></div>
                            )}
                        </div>
                    );
                }

            case 'expectations':
                if (!analystExpectations) return null;
                
                const formatPercentage = (value) => {
                    if (value === null || value === undefined) return 'N/A';
                    const sign = value > 0 ? '+' : '';
                    return `${sign}${value.toFixed(1)}%`;
                };

                const formatCurrency = (value) => {
                    if (value === null || value === undefined) return 'N/A';
                    if (value >= 1e9) return `$${(value / 1e9).toFixed(1)}B`;
                    if (value >= 1e6) return `$${(value / 1e6).toFixed(1)}M`;
                    return `$${value.toFixed(2)}`;
                };

                const getChangeColor = (value) => {
                    if (value === null || value === undefined) return 'rgba(255, 255, 255, 0.6)';
                    return value > 0 ? '#4ade80' : value < 0 ? '#f87171' : 'rgba(255, 255, 255, 0.6)';
                };

                return (
                    <div className="grid-card-content">
                        <div style={{ 
                            display: 'flex', 
                            flexDirection: 'column', 
                            gap: '12px',
                            height: '100%'
                        }}>
                            {/* Header with Quarter Info */}
                            <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                paddingBottom: '8px',
                                borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
                            }}>
                                <div>
                                    <div style={{
                                        fontSize: '0.9rem',
                                        fontWeight: '600',
                                        color: 'white',
                                        marginBottom: '2px'
                                    }}>
                                        {analystExpectations.reporting_quarter}
                                    </div>
                                    <div style={{
                                        fontSize: '0.7rem',
                                        color: 'rgba(255, 255, 255, 0.6)'
                                    }}>
                                        Report: {new Date(analystExpectations.earnings_report_date).toLocaleDateString()}
                                    </div>
                                </div>
                                <div style={{
                                    fontSize: '0.6rem',
                                    color: 'rgba(255, 255, 255, 0.5)',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.5px'
                                }}>
                                    Expectations vs Reality
                                </div>
                            </div>

                            {/* Revenue Section */}
                            <div style={{ flex: 1 }}>
                                <div style={{
                                    fontSize: '0.75rem',
                                    fontWeight: '500',
                                    color: 'rgba(255, 255, 255, 0.8)',
                                    marginBottom: '8px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '6px'
                                }}>
                                    <div style={{
                                        width: '3px',
                                        height: '10px',
                                        background: 'linear-gradient(180deg, #6b7280, #374151)',
                                        borderRadius: '2px'
                                    }} />
                                    REVENUE EXPECTATIONS
                                </div>
                                
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px' }}>
                                    {/* Current Estimate */}
                                    <div style={{
                                        background: 'rgba(255, 255, 255, 0.03)',
                                        border: '1px solid rgba(255, 255, 255, 0.08)',
                                        borderRadius: '6px',
                                        padding: '8px'
                                    }}>
                                        <div style={{
                                            fontSize: '0.6rem',
                                            color: 'rgba(255, 255, 255, 0.5)',
                                            marginBottom: '3px',
                                            textTransform: 'uppercase'
                                        }}>
                                            Estimate
                                        </div>
                                        <div style={{
                                            fontSize: '0.8rem',
                                            fontWeight: '600',
                                            color: 'white'
                                        }}>
                                            {formatCurrency(analystExpectations.current_estimate_revenue)}
                                        </div>
                                    </div>

                                    {/* Actual (if available) */}
                                    <div style={{
                                        background: analystExpectations.current_actual_revenue ? 'rgba(74, 222, 128, 0.1)' : 'rgba(255, 255, 255, 0.03)',
                                        border: `1px solid ${analystExpectations.current_actual_revenue ? 'rgba(74, 222, 128, 0.2)' : 'rgba(255, 255, 255, 0.08)'}`,
                                        borderRadius: '6px',
                                        padding: '8px'
                                    }}>
                                        <div style={{
                                            fontSize: '0.6rem',
                                            color: 'rgba(255, 255, 255, 0.5)',
                                            marginBottom: '3px',
                                            textTransform: 'uppercase'
                                        }}>
                                            Actual
                                        </div>
                                        <div style={{
                                            fontSize: '0.8rem',
                                            fontWeight: '600',
                                            color: analystExpectations.current_actual_revenue ? '#4ade80' : 'rgba(255, 255, 255, 0.4)'
                                        }}>
                                            {analystExpectations.current_actual_revenue ? formatCurrency(analystExpectations.current_actual_revenue) : 'Pending'}
                                        </div>
                                    </div>

                                    {/* Expected Changes */}
                                    <div style={{
                                        background: 'rgba(255, 255, 255, 0.03)',
                                        border: '1px solid rgba(255, 255, 255, 0.08)',
                                        borderRadius: '6px',
                                        padding: '8px',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: '4px'
                                    }}>
                                        <div style={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center'
                                        }}>
                                            <span style={{
                                                fontSize: '0.6rem',
                                                color: 'rgba(255, 255, 255, 0.6)'
                                            }}>
                                                QoQ Expected
                                            </span>
                                            <span style={{
                                                fontSize: '0.65rem',
                                                fontWeight: '600',
                                                color: getChangeColor(analystExpectations.qoq_estimate_revenue_change)
                                            }}>
                                                {formatPercentage(analystExpectations.qoq_estimate_revenue_change)}
                                            </span>
                                        </div>
                                        <div style={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center'
                                        }}>
                                            <span style={{
                                                fontSize: '0.6rem',
                                                color: 'rgba(255, 255, 255, 0.6)'
                                            }}>
                                                YoY Expected
                                            </span>
                                            <span style={{
                                                fontSize: '0.65rem',
                                                fontWeight: '600',
                                                color: getChangeColor(analystExpectations.yoy_estimate_revenue_change)
                                            }}>
                                                {formatPercentage(analystExpectations.yoy_estimate_revenue_change)}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* EPS Section */}
                            <div style={{ flex: 1 }}>
                                <div style={{
                                    fontSize: '0.75rem',
                                    fontWeight: '500',
                                    color: 'rgba(255, 255, 255, 0.8)',
                                    marginBottom: '8px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '6px'
                                }}>
                                    <div style={{
                                        width: '3px',
                                        height: '10px',
                                        background: 'linear-gradient(180deg, #6b7280, #374151)',
                                        borderRadius: '2px'
                                    }} />
                                    EPS EXPECTATIONS
                                </div>
                                
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px' }}>
                                    {/* Current Estimate */}
                                    <div style={{
                                        background: 'rgba(255, 255, 255, 0.03)',
                                        border: '1px solid rgba(255, 255, 255, 0.08)',
                                        borderRadius: '6px',
                                        padding: '8px'
                                    }}>
                                        <div style={{
                                            fontSize: '0.6rem',
                                            color: 'rgba(255, 255, 255, 0.5)',
                                            marginBottom: '3px',
                                            textTransform: 'uppercase'
                                        }}>
                                            Estimate
                                        </div>
                                        <div style={{
                                            fontSize: '0.8rem',
                                            fontWeight: '600',
                                            color: 'white'
                                        }}>
                                            {formatCurrency(analystExpectations.current_estimate_eps)}
                                        </div>
                                    </div>

                                    {/* Actual (if available) */}
                                    <div style={{
                                        background: analystExpectations.current_actual_eps ? 'rgba(74, 222, 128, 0.1)' : 'rgba(255, 255, 255, 0.03)',
                                        border: `1px solid ${analystExpectations.current_actual_eps ? 'rgba(74, 222, 128, 0.2)' : 'rgba(255, 255, 255, 0.08)'}`,
                                        borderRadius: '6px',
                                        padding: '8px'
                                    }}>
                                        <div style={{
                                            fontSize: '0.6rem',
                                            color: 'rgba(255, 255, 255, 0.5)',
                                            marginBottom: '3px',
                                            textTransform: 'uppercase'
                                        }}>
                                            Actual
                                        </div>
                                        <div style={{
                                            fontSize: '0.8rem',
                                            fontWeight: '600',
                                            color: analystExpectations.current_actual_eps ? '#4ade80' : 'rgba(255, 255, 255, 0.4)'
                                        }}>
                                            {analystExpectations.current_actual_eps ? formatCurrency(analystExpectations.current_actual_eps) : 'Pending'}
                                        </div>
                                    </div>

                                    {/* Expected Changes */}
                                    <div style={{
                                        background: 'rgba(255, 255, 255, 0.03)',
                                        border: '1px solid rgba(255, 255, 255, 0.08)',
                                        borderRadius: '6px',
                                        padding: '8px',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: '4px'
                                    }}>
                                        <div style={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center'
                                        }}>
                                            <span style={{
                                                fontSize: '0.6rem',
                                                color: 'rgba(255, 255, 255, 0.6)'
                                            }}>
                                                QoQ Expected
                                            </span>
                                            <span style={{
                                                fontSize: '0.65rem',
                                                fontWeight: '600',
                                                color: getChangeColor(analystExpectations.qoq_estimate_eps_change)
                                            }}>
                                                {formatPercentage(analystExpectations.qoq_estimate_eps_change)}
                                            </span>
                                        </div>
                                        <div style={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center'
                                        }}>
                                            <span style={{
                                                fontSize: '0.6rem',
                                                color: 'rgba(255, 255, 255, 0.6)'
                                            }}>
                                                YoY Expected
                                            </span>
                                            <span style={{
                                                fontSize: '0.65rem',
                                                fontWeight: '600',
                                                color: getChangeColor(analystExpectations.yoy_estimate_eps_change)
                                            }}>
                                                {formatPercentage(analystExpectations.yoy_estimate_eps_change)}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <style jsx>{`
                            @keyframes slideIn {
                                from {
                                    width: 0%;
                                    opacity: 0;
                                }
                                to {
                                    opacity: 1;
                                }
                            }
                        `}</style>
                    </div>
                );

            case 'news':
                return (
                    <div className="grid-card-content">
                        {relatedNews.length > 0 ? (
                            <div className="news-items">
                                {relatedNews.slice(0, 8).map((article, index) => {
                                    const link = article.link || article.url;
                                    const cleaned = link ? cleanNewsLink(link) : null;
                                    const thumb = getNewsThumbnailSrc(article);
                                    if (!cleaned) return null;
                                    return (
                                        <a 
                                            key={index}
                                            className="news-item news-card"
                                            href={cleaned}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            title={article.title}
                                        >
                                            {thumb && (
                                                <span className="news-thumb" aria-hidden="true">
                                                    <img 
                                                        src={thumb} 
                                                        alt="" 
                                                        loading="lazy"
                                                        onError={(e) => {
                                                            if (e?.currentTarget?.parentElement) {
                                                                e.currentTarget.parentElement.style.display = 'none';
                                                            }
                                                        }}
                                                    />
                                                </span>
                                            )}
                                            <div className="news-content">
                                                <h5 className="news-title">{article.title}</h5>
                                                {article.desc && (
                                                    <p className="news-summary">{article.desc}</p>
                                                )}
                                                <div className="news-meta">
                                                    {article.media && <span className="news-chip">{article.media}</span>}
                                                    {article.media && article.date && <span className="news-dot">•</span>}
                                                    {article.date && <span className="news-period">{article.date}</span>}
                                                </div>
                                            </div>
                                            <span className="news-chevron" aria-hidden="true">
                                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                                    <path d="M9 6l6 6-6 6"/>
                                                </svg>
                                            </span>
                                        </a>
                                    );
                                })}
                            </div>
                ) : (
                    <div className="empty-state">
                                <p>{errorStates.news ? 'Failed to load news' : 'No recent news found'}</p>
                    </div>
                )}
        </div>
        );

            case 'image':
                // Only show images for keywords, not companies
                if (!activeItem || activeItem.type !== 'keyword') return null;
                
                const keyword = activeItem.data;
                return (
                    <div className="grid-card-content">
                        <GoogleImageDisplay 
                            keyword={keyword.keyword_name}
                            className="keyword-image-display"
                            style={{ width: '100%', height: '100%' }}
                        />
                    </div>
                );

            case 'definition':
                // Only show definition for keywords, not companies
                if (!activeItem || activeItem.type !== 'keyword') return null;
                
                return (
                    <div className="grid-card-content">
                        {loadingStates.definition ? (
                            <div className="loading-spinner-container">
                                <div className="loading-spinner"></div>
                            </div>
                        ) : keywordDefinition ? (
                            <div className="definition-content">
                                <p className="definition-text">
                                    {formatDefinitionText(keywordDefinition.extract)}
                                </p>
                            </div>
                        ) : errorStates.definition ? (
                            <div className="empty-state">
                                <p>No definition available</p>
                            </div>
                        ) : null}
                    </div>
                );

            default:
                return <div>Component not found</div>;
        }
    };


    return (
        <>
            {/* Sidebar Toggle Button (only when not stacked) */}
            {!isStacked && (
                <button 
                    type="button"
                    className="sidebar-toggle" 
                    onClick={(e) => { 
                        e.stopPropagation(); 
                        toggleSidebar();
                    }}
                    title={isOpen ? "Close Insights" : "Open Insights"}
                    aria-label={isOpen ? "Close Insights" : "Open Insights"}
                >
                    <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" fill="none">
                        <path fill="none" stroke="currentColor" strokeWidth="2" d="M1,22 L23,22 L23,2 L1,2 L1,22 Z M16,2 L16,22 L16,2 Z"/>
                    </svg>
                </button>
            )}

            {/* Backdrop (mobile only, only when not stacked) */}
            {!isStacked && (
                <div 
                    className={`sidebar-backdrop ${isOpen ? 'visible' : ''}`}
                    onClick={closeSidebar}
                    role="button"
                    aria-label="Close Insights Backdrop"
                />
            )}

            {/* Sidebar */}
            <div className={`insights-sidebar-redesigned ${isStacked ? 'stacked' : ''} ${isOpen ? 'open' : ''}`}>
                {/* Sidebar Header (only when not stacked) */}
                {!isStacked && (
                    <div className="sidebar-header">
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <div style={{
                                width: '4px',
                                height: '20px',
                                background: 'linear-gradient(180deg, #ffffff, #9ca3af)',
                                borderRadius: '2px'
                            }} />
                            <h2 style={{
                                fontSize: '1.1rem',
                                fontWeight: '600',
                                color: '#ffffff',
                                letterSpacing: '0.025em',
                                textTransform: 'uppercase',
                                margin: 0
                            }}>
                                Insights
                            </h2>
                        </div>
                        <button 
                            type="button"
                            className="sidebar-close" 
                            onClick={(e) => { e.stopPropagation(); closeSidebar(); }} 
                            title="Close sidebar"
                            aria-label="Close sidebar"
                        >
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                <path d="M18 6L6 18M6 6l12 12"/>
                            </svg>
                        </button>
                    </div>
                )}
                
                {/* When stacked, add a simple header without close button */}
                {isStacked && (
                    <div className="stacked-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '0', paddingTop: '0.5rem' }}>
                        <h3 style={{
                            fontSize: '1.1rem',
                            fontWeight: '600',
                            color: '#ffffff',
                            letterSpacing: '0.025em',
                            textTransform: 'uppercase',
                            margin: 0,
                            textAlign: 'center'
                        }}>
                            Insights
                        </h3>
                    </div>
                )}

                {/* Sidebar Content - Only render when open or stacked */}
                {(isOpen || isStacked) && (
                    <div className="insights-content" ref={contentRef} onScroll={handleOuterScroll}>
                        <HeroCard />
                        
                        {/* Grid Layout for Components */}
                        {activeItem && (
                            <div className="insights-grid-container">
                                <ResponsiveGridLayout
                                    className="insights-grid-layout"
                                    layouts={gridLayouts}
                                    breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 480 }}
                                    cols={{ lg: 1, md: 1, sm: 1, xs: 1 }}
                                    rowHeight={100}
                                    margin={[8, 8]}
                                    containerPadding={[0, 0]}
                                    onLayoutChange={handleLayoutChange}
                                    isDraggable={true}
                                    isResizable={true}
                                    autoSize={true}
                                    useCSSTransforms={true}
                                    allowOverlap={false}
                                    draggableHandle=".grid-drag-handle"
                                    resizeHandles={['s']}
                                    compactType="vertical"
                                >
                                    {gridComponents.map(component => (
                                        <div 
                                            key={component.id} 
                                            className="insights-grid-item"
                                        >
                                            <div className="grid-card">
                                                <div className="grid-card-header">
                                                    <h3>{component.title}</h3>
                                                    <div className="grid-card-controls">
                                                        <button className="grid-drag-handle" title="Drag to move">
                                                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                                <circle cx="9" cy="12" r="1"/>
                                                                <circle cx="9" cy="5" r="1"/>
                                                                <circle cx="9" cy="19" r="1"/>
                                                                <circle cx="15" cy="12" r="1"/>
                                                                <circle cx="15" cy="5" r="1"/>
                                                                <circle cx="15" cy="19" r="1"/>
                                                            </svg>
                                                        </button>
                                                    </div>
                                                </div>
                                                {renderGridComponent(component.id)}
                                            </div>
                                        </div>
                                    ))}
                                </ResponsiveGridLayout>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </>
    );
};

InsightsSidebar.propTypes = {
    isAuthenticated: PropTypes.bool.isRequired,
    isStacked: PropTypes.bool,
    parentIsOpen: PropTypes.bool,
    onParentClose: PropTypes.func,
};

export default InsightsSidebar; 